import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

interface QueryAtmEventsPayload {
  skip?: number;
  take?: number;
  search?: string;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  [key: string]: any;
}

interface QueryAtmEventsResponse {
  data: any[];
  total: number;
}

const getAtmsApi = async (): Promise<any[]> => {
  const response = await httpClient.get('api/atm/get', {});
  const data = response.data;
  return data?.atms ?? data?.data ?? (Array.isArray(data) ? data : []);
};

const queryAtmEventsApi = async (payload: QueryAtmEventsPayload): Promise<QueryAtmEventsResponse> => {
  const response = await httpClient.post<QueryAtmEventsResponse>('api/atm/query', payload);
  return response.data;
};

const updateAtmEventApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/atm/update', payload);
  return response.data;
};

const updateAtmAssetApi = async (payload: any): Promise<any> => {
  const response = await httpClient.post('api/atm/update-asset', payload);
  return response.data;
};

const createAtmAssetApi = async (payload: { assetId: string; locationId: number; position?: number; active?: boolean }): Promise<any> => {
  const response = await httpClient.post('api/atm/create-asset', payload);
  return response.data;
};

export const useAtms = () => {
  return useQuery({
    queryKey: ['atms'],
    queryFn: getAtmsApi,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useQueryAtmEvents = () => {
  return useMutation({
    mutationFn: queryAtmEventsApi,
  });
};

export const useUpdateAtmEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAtmEventApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['atm-events'] });
      queryClient.invalidateQueries({ queryKey: ['location-readings'] });
    },
  });
};

export const useUpdateAtmAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAtmAssetApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['atms'] });
      queryClient.invalidateQueries({ queryKey: ['location-details'] });
    },
  });
};

export const useCreateAtmAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAtmAssetApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['atms'] });
      queryClient.invalidateQueries({ queryKey: ['location-details'] });
    },
  });
};
